import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { CheckCircle, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { RootState } from '../../reducers';
import { sendEmail } from '../../services/emailService';
import { wrapWithTheme } from '../email/templates';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';

interface FormData {
  name: string;
  email: string;
  company: string;
  teamSize: string;
  trialPurpose: string;
  whyScreenApp: string;
  phone: string;
}

interface LocationInfo {
  country?: string;
  city?: string;
}

interface ContactSalesFormProps {
  formSource?: string;
}

const ContactSalesForm: React.FC<ContactSalesFormProps> = ({ formSource }) => {
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.settings);
  const { subscription, usage, marketing } = useSelector(
    (state: RootState) => state.plan,
  );
  const { selectedTeam } = useSelector((state: RootState) => state.team);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    name: user?.name || '',
    email: user?.email || '',
    company: '',
    teamSize: '',
    trialPurpose: '',
    whyScreenApp: '',
    phone: '',
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [locationInfo, setLocationInfo] = useState<LocationInfo>({});

  useEffect(() => {
    // Get location information when component mounts
    fetch('https://ipapi.co/json/')
      .then((res) => res.json())
      .then((data) => {
        setLocationInfo({
          country: data.country_name,
          city: data.city,
        });
      })
      .catch((error) => {
        console.error('Failed to get location info:', error);
      });
  }, []);

  const validateForm = () => {
    const newErrors: Partial<FormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('sales.form.errors.name');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('sales.form.errors.email.required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('sales.form.errors.email.invalid');
    }

    if (!formData.teamSize.trim()) {
      newErrors.teamSize = t('sales.form.errors.teamSize');
    }

    if (!formData.phone.trim()) {
      newErrors.phone = t('sales.form.errors.phone');
    }

    // Only validate trial purpose if it's from the Request Trial button
    if (
      formSource === 'Dashboard - Request Trial' &&
      !formData.trialPurpose.trim()
    ) {
      newErrors.trialPurpose = t('sales.form.errors.trialPurpose');
    }

    // Make whyScreenApp required when not showing trial purpose
    if (
      formSource !== 'Dashboard - Request Trial' &&
      !formData.whyScreenApp.trim()
    ) {
      newErrors.whyScreenApp = t('sales.form.errors.whyScreenApp');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);
      try {
        const emailContent = `
          <h2>New Lead from Contact Form</h2>
          
          <table style="width: 100%; border-collapse: collapse;">
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Form Source:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                formSource || 'Direct Contact'
              }</td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Name:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                formData.name
              }</td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Email:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                formData.email
              }</td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Phone:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                formData.phone
              }</td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Company:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                formData.company || 'Not provided'
              }</td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Team Size:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                formData.teamSize
              }</td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Trial Purpose:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                formData.trialPurpose
              }</td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Why ScreenApp:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                formData.whyScreenApp || 'Not provided'
              }</td>
            </tr>
            ${
              locationInfo.country || locationInfo.city
                ? `
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Location:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${[
                locationInfo.city,
                locationInfo.country,
              ]
                .filter(Boolean)
                .join(', ')}</td>
            </tr>
            `
                : ''
            }
            ${
              marketing?.intent
                ? `
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Initial Intent:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${marketing.intent}</td>
            </tr>
            `
                : ''
            }

            <tr>
              <td colspan="2" style="padding: 24px 0 12px 0;"><strong>User Information</strong></td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Current Plan:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                subscription?.package || 'No subscription'
              } (${subscription?.term || 'N/A'})</td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Current Price:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">$${
                subscription?.price || 0
              }</td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Subscription Since:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                subscription?.currentPeriodStartedAt
                  ? new Date(
                      subscription.currentPeriodStartedAt,
                    ).toLocaleDateString()
                  : 'N/A'
              }</td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Stripe ID:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                subscription?.providerId || 'N/A'
              }</td>
            </tr>

            <tr>
              <td colspan="2" style="padding: 24px 0 12px 0;"><strong>Usage Statistics</strong></td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Total Uploads:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                usage?.uploadCount || 0
              }</td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>AI Interactions:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                usage?.askAICount || 0
              }</td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Video AI Interactions:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                usage?.videoAskAICount || 0
              }</td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Transcriptions:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                usage?.transcriptionCount || 0
              }</td>
            </tr>

            <tr>
              <td colspan="2" style="padding: 24px 0 12px 0;"><strong>Team Information</strong></td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Team Name:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                selectedTeam?.name || 'No team'
              }</td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Team Members:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                selectedTeam?.teamSpaceMembers?.length || 0
              }</td>
            </tr>
            <tr>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;"><strong>Team Created:</strong></td>
              <td style="padding: 12px 0; border-bottom: 1px solid #eee;">${
                selectedTeam?.createdAt
                  ? new Date(selectedTeam.createdAt).toLocaleDateString()
                  : 'N/A'
              }</td>
            </tr>

            <tr>
              <td colspan="2" style="padding: 24px 0 12px 0;"><strong>System Information</strong></td>
            </tr>
            <tr>
              <td style="padding: 12px 0;"><strong>OS:</strong></td>
              <td style="padding: 12px 0;">${window.navigator.platform}</td>
            </tr>
            <tr>
              <td style="padding: 12px 0;"><strong>Browser:</strong></td>
              <td style="padding: 12px 0;">${window.navigator.userAgent}</td>
            </tr>
          </table>
        `;

        // Send to both Andre and Aditya
        await Promise.all([
          sendEmail({
            template: 'genericEmail',
            toEmail: 'andre@screenapp.io',
            data: {
              content: wrapWithTheme(emailContent),
              subject: `New Lead: ${formData.name} from ${
                formData.company || 'Unknown Company'
              }`,
            },
          }),
          sendEmail({
            template: 'genericEmail',
            toEmail: 'aditya@screenapp.io',
            data: {
              content: wrapWithTheme(emailContent),
              subject: `New Lead: ${formData.name} from ${
                formData.company || 'Unknown Company'
              }`,
            },
          }),
        ]);

        setIsSuccess(true);
        // Reset form after successful submission
        setFormData({
          name: '',
          email: '',
          company: '',
          teamSize: '',
          trialPurpose: '',
          whyScreenApp: '',
          phone: '',
        });
      } catch (error) {
        console.error('Failed to submit form:', error);
        toast.error('Failed to submit form. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  if (isSuccess) {
    return (
      <div className="flex flex-col items-center justify-center py-8 px-4">
        <div className="rounded-full bg-green-50 p-2 mb-3">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold mb-1">
          {t('sales.form.success.title')}
        </h3>
        <p className="text-sm text-muted-foreground text-center max-w-md">
          {t('sales.form.success.message')}
        </p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="name">
              {t('sales.form.labels.name')}
              <span className="text-destructive ml-0.5">*</span>
            </Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={
                errors.name
                  ? 'border-destructive focus-visible:ring-destructive'
                  : ''
              }
              placeholder="John Doe"
            />
            {errors.name && (
              <p className="text-[0.8rem] text-destructive mt-1.5">
                {errors.name}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">
              {t('sales.form.labels.email')}
              <span className="text-destructive ml-0.5">*</span>
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              className={
                errors.email
                  ? 'border-destructive focus-visible:ring-destructive'
                  : ''
              }
              placeholder="john@example.com"
            />
            {errors.email && (
              <p className="text-[0.8rem] text-destructive mt-1.5">
                {errors.email}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">
              {t('sales.form.labels.phone')}
              <span className="text-destructive ml-0.5">*</span>
            </Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              className={
                errors.phone
                  ? 'border-destructive focus-visible:ring-destructive'
                  : ''
              }
              placeholder="+1 (555) 000-0000"
            />
            {errors.phone && (
              <p className="text-[0.8rem] text-destructive mt-1.5">
                {errors.phone}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="company">{t('sales.form.labels.company')}</Label>
            <Input
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              placeholder="Acme Inc."
            />
          </div>

          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="teamSize">
              {t('sales.form.labels.teamSize')}
              <span className="text-destructive ml-0.5">*</span>
            </Label>
            <Input
              id="teamSize"
              name="teamSize"
              value={formData.teamSize}
              onChange={handleChange}
              className={
                errors.teamSize
                  ? 'border-destructive focus-visible:ring-destructive'
                  : ''
              }
              placeholder="e.g., 10-50 employees"
            />
            {errors.teamSize && (
              <p className="text-[0.8rem] text-destructive mt-1.5">
                {errors.teamSize}
              </p>
            )}
          </div>
        </div>

        {formSource === 'Dashboard - Request Trial' && (
          <div className="space-y-2">
            <Label htmlFor="trialPurpose">
              {t('sales.form.labels.trialPurpose')}
              <span className="text-destructive ml-0.5">*</span>
            </Label>
            <Input
              id="trialPurpose"
              name="trialPurpose"
              value={formData.trialPurpose}
              onChange={handleChange}
              className={
                errors.trialPurpose
                  ? 'border-destructive focus-visible:ring-destructive'
                  : ''
              }
              placeholder="What would you like to achieve with the trial?"
            />
            {errors.trialPurpose && (
              <p className="text-[0.8rem] text-destructive mt-1.5">
                {errors.trialPurpose}
              </p>
            )}
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="whyScreenApp">
            {t('sales.form.labels.whyScreenApp')}
            {formSource !== 'Dashboard - Request Trial' && (
              <span className="text-destructive ml-0.5">*</span>
            )}
          </Label>
          <Textarea
            id="whyScreenApp"
            name="whyScreenApp"
            value={formData.whyScreenApp}
            onChange={handleChange}
            rows={3}
            className={`resize-none ${errors.whyScreenApp ? 'border-destructive focus-visible:ring-destructive' : ''}`}
            placeholder="Tell us about your use case..."
          />
          {errors.whyScreenApp && (
            <p className="text-[0.8rem] text-destructive mt-1.5">
              {errors.whyScreenApp}
            </p>
          )}
        </div>

        <div className="flex justify-end pt-2">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader className="mr-2 h-4 w-4 animate-spin text-background" />
                {t('sales.form.buttons.sending')}
              </>
            ) : (
              t('sales.form.buttons.send')
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ContactSalesForm;
